import type { StrategyEntry } from "../../../services/investorStrategyService";
import {
  getStrategyDisplayName,
  type ProductLookupEntry,
  type ProductLookupKey,
  type StrategyDraftEntry,
} from "../../../utils/strategyUtils";

/**
 * One product slice for the allocation-methodology gate animation.
 * `setPct` is the weight the user set (allocated); `currentPct` is where the
 * money sits today (drifted). New cash follows `currentPct`, not `setPct`.
 */
export interface AllocationSlice {
  key: string;
  label: string;
  setPct: number;
  currentPct: number;
}

export interface AllocationSplit {
  slices: AllocationSlice[];
  /** True when any slice's current weight differs from the weight it was set to. */
  hasDrift: boolean;
}

// Drift below this (percentage points) reads as "no drift yet" for the copy.
const DRIFT_EPSILON = 0.05;
const OTHERS_KEY = "others";
const OTHERS_LABEL = "Others";

/**
 * Build the split the gate animation cites.
 *
 * Source of "current %": the user's real holdings (`currentStrategies`), per
 * position `driftedWeight ?? parseFloat(weight)` -- the existing fallback (see
 * `StrategyPage.tsx`, `StrategyDesktopTable.tsx`): drifted weight when traded,
 * allocated weight when not yet traded. For a first-time strategy (no holdings
 * yet) it falls back to the draft being saved, so current == allocated and
 * there is always a real split to show. Slices are collapsed to the top N by
 * current weight plus an aggregated "Others" remainder so the bar stays legible.
 */
export function buildAllocationSplit(
  currentStrategies: StrategyEntry[],
  draftEntries: StrategyDraftEntry[],
  productLookup?: Map<ProductLookupKey, ProductLookupEntry>,
  topN = 3
): AllocationSplit {
  const lookup = productLookup ?? new Map<ProductLookupKey, ProductLookupEntry>();

  const raw: AllocationSlice[] =
    currentStrategies.length > 0
      ? currentStrategies.map((entry) => {
          const parsedSet = parseFloat(entry.weight);
          const setPct = Number.isFinite(parsedSet) ? parsedSet : 0;
          return {
            key: `${entry.entityType}-${entry.productId}`,
            label: getStrategyDisplayName(entry, lookup),
            setPct,
            currentPct: entry.driftedWeight ?? setPct,
          };
        })
      : draftEntries.map((entry) => ({
          key: `${entry.entityType}-${entry.productId}`,
          label: entry.displayName,
          setPct: entry.weight,
          currentPct: entry.weight,
        }));

  const meaningful = raw.filter((slice) => slice.currentPct > 0 || slice.setPct > 0);
  const hasDrift = meaningful.some(
    (slice) => Math.abs(slice.currentPct - slice.setPct) > DRIFT_EPSILON
  );

  const sorted = [...meaningful].sort((a, b) => b.currentPct - a.currentPct);
  if (sorted.length <= topN) {
    return { slices: sorted, hasDrift };
  }

  const head = sorted.slice(0, topN);
  const tail = sorted.slice(topN);
  const others: AllocationSlice = {
    key: OTHERS_KEY,
    label: OTHERS_LABEL,
    setPct: tail.reduce((sum, slice) => sum + slice.setPct, 0),
    currentPct: tail.reduce((sum, slice) => sum + slice.currentPct, 0),
  };
  return { slices: [...head, others], hasDrift };
}
